import React, { useState } from 'react';
import { DashboardFilters } from '../components/dashboard/DashboardFilters';
import { AlertBanner } from '../components/dashboard/AlertBanner';
import { PerformanceMetrics } from '../components/dashboard/PerformanceMetrics';
import { ConversionRatios } from '../components/dashboard/ConversionRatios';
import { ComparisonTable } from '../components/dashboard/ComparisonTable';
import { TrendChart } from '../components/dashboard/TrendChart';
import { DrillDownModal } from '../components/dashboard/DrillDownModal';
import { DashboardFilterState, User, MetricData, ConversionRatio, TrendPoint } from '../types/dashboard';
// --- Mock Data ---
const MOCK_USERS: User[] = [{
  id: 'u1',
  name: 'Alex Thompson',
  role: 'Loan Officer'
}, {
  id: 'u2',
  name: 'Sarah Miller',
  role: 'Loan Officer'
}, {
  id: 'u3',
  name: 'James Wilson',
  role: 'Client Care Specialist'
}, {
  id: 'u4',
  name: 'Emily Chen',
  role: 'Client Care Specialist'
}];
const MOCK_METRICS: Record<string, MetricData> = {
  u1: {
    calls: 145,
    transfers: 32,
    applicationsTaken: 18,
    documentsOut: 15,
    submissions: 12
  },
  u2: {
    calls: 160,
    transfers: 28,
    applicationsTaken: 22,
    documentsOut: 20,
    submissions: 18
  },
  u3: {
    calls: 210,
    transfers: 45,
    applicationsTaken: 5,
    documentsOut: 3,
    submissions: 1
  },
  u4: {
    calls: 195,
    transfers: 40,
    applicationsTaken: 8,
    documentsOut: 6,
    submissions: 2
  }
};
const MOCK_TRENDS: TrendPoint[] = [{
  date: 'Oct 1',
  calls: 45,
  applications: 5,
  submissions: 2,
  transfers: 10,
  documents: 4
}, {
  date: 'Oct 8',
  calls: 52,
  applications: 7,
  submissions: 3,
  transfers: 12,
  documents: 6
}, {
  date: 'Oct 15',
  calls: 48,
  applications: 6,
  submissions: 4,
  transfers: 15,
  documents: 5
}, {
  date: 'Oct 22',
  calls: 60,
  applications: 9,
  submissions: 5,
  transfers: 18,
  documents: 8
}, {
  date: 'Oct 29',
  calls: 55,
  applications: 8,
  submissions: 6,
  transfers: 14,
  documents: 7
}];
// Helper to aggregate metrics for multiple users
const aggregateMetrics = (userIds: string[]): MetricData => {
  const targetIds = userIds.length > 0 ? userIds : MOCK_USERS.map(u => u.id);
  return targetIds.reduce((acc, id) => {
    const m = MOCK_METRICS[id] || {
      calls: 0,
      transfers: 0,
      applicationsTaken: 0,
      documentsOut: 0,
      submissions: 0
    };
    return {
      calls: acc.calls + m.calls,
      transfers: acc.transfers + m.transfers,
      applicationsTaken: acc.applicationsTaken + m.applicationsTaken,
      documentsOut: acc.documentsOut + m.documentsOut,
      submissions: acc.submissions + m.submissions
    };
  }, {
    calls: 0,
    transfers: 0,
    applicationsTaken: 0,
    documentsOut: 0,
    submissions: 0
  });
};
// Helper to calculate ratios
const calculateRatios = (metrics: MetricData): ConversionRatio[] => {
  const safeDiv = (n: number, d: number) => d === 0 ? 0 : n / d * 100;
  return [{
    label: 'Apps → Docs',
    value: safeDiv(metrics.documentsOut, metrics.applicationsTaken),
    fromMetric: 'Apps',
    toMetric: 'Docs',
    threshold: 75
  }, {
    label: 'Docs → Subs',
    value: safeDiv(metrics.submissions, metrics.documentsOut),
    fromMetric: 'Docs',
    toMetric: 'Subs',
    threshold: 60
  }, {
    label: 'Calls → Apps',
    value: safeDiv(metrics.applicationsTaken, metrics.calls),
    fromMetric: 'Calls',
    toMetric: 'Apps',
    threshold: 10
  }, {
    label: 'Calls → Transfers',
    value: safeDiv(metrics.transfers, metrics.calls),
    fromMetric: 'Calls',
    toMetric: 'Transfers',
    threshold: 20
  }, {
    label: 'Calls → Subs',
    value: safeDiv(metrics.submissions, metrics.calls),
    fromMetric: 'Calls',
    toMetric: 'Subs',
    threshold: 5
  }, {
    label: 'Transfers → Apps',
    value: safeDiv(metrics.applicationsTaken, metrics.transfers),
    fromMetric: 'Transfers',
    toMetric: 'Apps',
    threshold: 40
  }];
};
export function AdminDashboard() {
  const [filters, setFilters] = useState<DashboardFilterState>({
    dateRange: {
      start: new Date(),
      end: new Date(),
      label: 'Today'
    },
    selectedUsers: [],
    preset: 'Today'
  });
  const [drillDown, setDrillDown] = useState<{
    isOpen: boolean;
    metric: string;
  }>({
    isOpen: false,
    metric: ''
  });
  const currentMetrics = aggregateMetrics(filters.selectedUsers);
  const currentRatios = calculateRatios(currentMetrics);
  const comparisonUsers = filters.selectedUsers.length > 0 ? MOCK_USERS.filter(u => filters.selectedUsers.includes(u.id)) : MOCK_USERS;
  const comparisonRatios = comparisonUsers.reduce((acc, user) => {
    acc[user.id] = calculateRatios(MOCK_METRICS[user.id]);
    return acc;
  }, {} as Record<string, ConversionRatio[]>);
  const handleRefresh = () => {
    console.log('Refreshing cache...');
  };
  const handleExport = () => {
    console.log('Exporting data...');
    alert('Export started. You will receive a notification when ready.');
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-206">
      <DashboardFilters filters={filters} onFilterChange={setFilters} users={MOCK_USERS} lastRefreshed={new Date()} onRefresh={handleRefresh} onExport={handleExport} data-id="element-207" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-208">
        {/* Header */}
        <div data-id="element-209">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-210">
            Performance Overview
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-211">
            Analyzing{' '}
            {filters.selectedUsers.length > 0 ? `${filters.selectedUsers.length} selected users` : 'all team members'}{' '}
            for {filters.preset}
          </p>
        </div>

        {/* Alert Banner - NEW */}
        <section data-id="element-212">
          <AlertBanner data-id="element-213" />
        </section>

        {/* Top Level Metrics */}
        <section data-id="element-214">
          <PerformanceMetrics data={currentMetrics} onDrillDown={metric => setDrillDown({
          isOpen: true,
          metric
        })} data-id="element-215" />
        </section>

        {/* Deep Dive Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-216">
          {/* Left Column: Ratios & Trends */}
          <div className="lg:col-span-2 space-y-8" data-id="element-217">
            <TrendChart data={MOCK_TRENDS} users={comparisonUsers} data-id="element-218" />

            <ComparisonTable users={comparisonUsers} metrics={MOCK_METRICS} ratios={comparisonRatios} data-id="element-219" />
          </div>

          {/* Right Column: Ratios Cards */}
          <div className="lg:col-span-1" data-id="element-220">
            <ConversionRatios ratios={currentRatios} data-id="element-221" />
          </div>
        </div>
      </main>

      <DrillDownModal isOpen={drillDown.isOpen} onClose={() => setDrillDown({
      ...drillDown,
      isOpen: false
    })} title="Metric Breakdown" metric={drillDown.metric} data-id="element-222" />
    </div>;
}